import React, { useState } from "react";

import {
    Check as CheckIcon,
    Error as ErrorIcon,
    PlayArrow as PlayArrowIcon,
} from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Paper,
    Typography,
} from "@mui/material";

import { allExpanded, defaultStyles, JsonView } from "react-json-view-lite";
import "react-json-view-lite/dist/index.css";

import { getB24TrackerEvent } from "helpers/Sources/b24/b24Source";
import { getHubspotTest, getZohoTest } from "helpers/Sources/createSource";
import { testDynamics } from "helpers/Sources/dynamics365/dynamics365";
import { getFreshSalesTest } from "helpers/Sources/freshSale/freshSales";
import { getLeadsquaredTest } from "helpers/Sources/leadsquared/leadsquared";
import { mondayTest } from "helpers/Sources/monday/monday";
import { getOdooTest } from "helpers/Sources/odoo/odoo";
import { getPipedriveTrackerEvent } from "helpers/Sources/pipedrive/pipedriveSource";
import { getSalesforceTest } from "helpers/Sources/salesForce/salesForce";
import { getVtigerTest } from "helpers/Sources/vtiger/vtiger";
import { getZendeskTrackerEvent } from "helpers/Sources/zendesk/zendeskSource";
import { getSourceTest } from "helpers/test/testSource";
import { getSourceTrackerEvent } from "helpers/trackers/getTrackerEvent";

import SwalAlertComp from "components/SwalAlert";

import { TabProps } from "../../../SourceModal.types";

interface TestResult {
    status: boolean;
    message?: string;
    data?: any;
}

const TestTab: React.FC<TabProps> = ({ source, tracker, language }) => {
    const [loading, setLoading] = useState(false);
    const [testResult, setTestResult] = useState<TestResult | null>(null);
    const [testDetails, setTestDetails] = useState<any>(null);

    const sourceCode = source?.source_type?.code;
    const trackerId = tracker?.tracker?.id;

    const getTestFunction = () => {
        switch (sourceCode) {
            case "zoho":
                return () => getZohoTest(source?.id);
            case "hub":
                return () => getHubspotTest(source?.id);
            case "freshsales":
                return () => getFreshSalesTest(source?.id);
            case "vtiger":
                return () => getVtigerTest(source?.id);
            case "odoo":
                return () => getOdooTest(source?.id);
            case "lsqrd":
                return () => getLeadsquaredTest(source?.id);
            case "salesforce":
                return () => getSalesforceTest(source?.id);
            case "dynamics":
                return () => testDynamics(source?.id);
            case "monday":
                return () => mondayTest(source?.id);
            case "zendesk_sell":
                return () => getZendeskTrackerEvent(source?.id);
            case "pipe":
                return () => getPipedriveTrackerEvent(source?.id);
            case "b24":
                return () => getB24TrackerEvent(source?.id);
            default:
                return async () => {
                    const response: any = await getSourceTrackerEvent(
                        trackerId,
                        source?.id,
                    );
                    if (response.status) {
                        const firstFileJobId =
                            response?.data?.fileJobs?.[0]?.id;
                        if (firstFileJobId) {
                            return await getSourceTest([firstFileJobId]);
                        }
                    }
                    return response;
                };
        }
    };

    const runTest = async () => {
        setLoading(true);
        setTestResult(null);
        setTestDetails(null);

        try {
            const testFn = getTestFunction();
            const response = await testFn();

            if (response?.status) {
                setTestResult({
                    status: true,
                    message: response.message || "Test successful!",
                });
                setTestDetails(response.data);
            } else {
                setTestResult({
                    status: false,
                    message: response?.message || "Test failed",
                });
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response?.message || "Source test failed",
                    icon: "error",
                });
            }
        } catch (error: any) {
            setTestResult({
                status: false,
                message: error.message || "Test failed",
            });
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: error.message || "An error occurred during the test",
                icon: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                {language?.buttons?.test ?? "Test Connection"}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
                Test your source connection to ensure data is being received
                correctly.
            </Typography>

            <Box sx={{ mb: 3 }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={runTest}
                    disabled={loading}
                    startIcon={
                        loading ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            <PlayArrowIcon />
                        )
                    }
                    sx={{ minWidth: 150 }}
                >
                    {loading ? "Testing..." : "Run Test"}
                </Button>
            </Box>

            {testDetails && (
                <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                    <Typography
                        variant="subtitle2"
                        gutterBottom
                        sx={{ fontWeight: 600 }}
                    >
                        Test Results
                    </Typography>
                    <Box
                        sx={{
                            maxHeight: 400,
                            overflow: "auto",
                            "& pre": { margin: 0 },
                        }}
                    >
                        <JsonView
                            data={testDetails}
                            shouldExpandNode={allExpanded}
                            style={defaultStyles}
                        />
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

export default TestTab;
